/**
 * @file schemas/audit-schedule.ts
 * @description 稽核任務「預計稽核日期」前後端共用 Zod Schema（客戶 PPT S39）。
 *
 * 業務規則：
 * - 稽核員收到派案後填寫【預計稽核日期】（具體日期 YYYY-MM-DD，非只有月份）。
 * - 可多次改期：該任務已有排程記錄時，reason 必填且至少 MIN_RESCHEDULE_REASON_LEN 字
 *   （條件必填由 API service 層校驗，schema 僅約束格式與長度上限）。
 * - 每次設定/改期都會通知管理組（admin/manager）。
 *
 * 獨立成檔原因：schemas/m2.ts 為其他會話在途檔案，避免衝突。
 */
import { z } from 'zod';
/** 改期時原因的最小字數（首次填寫免原因） */
export const MIN_RESCHEDULE_REASON_LEN = 5;
/**
 * 設定 / 改期預計稽核日期的請求體。
 * - plannedDate：具體日期，格式 YYYY-MM-DD（並校驗為真實存在的日期）
 * - reason：首次填寫可不填；改期時由 service 校驗必填（≥ MIN_RESCHEDULE_REASON_LEN 字）
 */
export const createAuditScheduleSchema = z.object({
    plannedDate: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, '格式須為 YYYY-MM-DD')
        .refine((v) => {
        const d = new Date(`${v}T00:00:00Z`);
        return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === v;
    }, '不是有效的日期'),
    reason: z.string().trim().max(500, '原因最多 500 字').optional(),
});
/**
 * 單筆排程記錄 DTO（GET /api/audit-tasks/:taskId/schedules 列表項，時間線用）。
 */
export const auditScheduleChangeItemSchema = z.object({
    /** 記錄 ID */
    id: z.number().int(),
    /** 關聯稽核任務 ID */
    auditTaskId: z.number().int(),
    /** 預計稽核日期（YYYY-MM-DD） */
    plannedDate: z.string(),
    /** 改期原因（首次填寫為 null） */
    reason: z.string().nullable(),
    /** 操作人 ID */
    createdBy: z.number().int(),
    /** 操作人姓名（join users；用戶已刪時為 null） */
    createdByName: z.string().nullable(),
    /** 建立時間（ISO 字串） */
    createdAt: z.string(),
});
